/**
 * Container component for the Orders table.
 */
import { useEffect, useState } from 'react';
import { useData } from '../../hooks/useData';
import { sortDataByColumn } from '../../utils/tables';
import type { TableColumn } from '../../types/TableColumn';
import TableLoader from './TableLoader';
import TableContainer from './TableContainer';
import TableHeader from './TableHeader';
import Table from './Table';
import RefreshButton from '../buttons/RefreshButton';
import FilterButton from '../buttons/FilterButton';
import ModalBasic from '../modals/ModalBasic';
import FilterModal from '../modals/FilterModal';
import TableBodyOrders from './TableBodyOrders';
import { downloadCsv } from '@/utils/csv';
import DownloadButton from '../buttons/DownloadButton';
import { formatSeconds } from '../../utils/time';
import { OrderItemTime } from '@/types/OrderItemTime';
import TableBodyItemTimes from './TableBodyItemTimes';
import TableHeadOrderItems from './TableHeadOrderItems';



type DataType = OrderItemTime[];

export default function OrdersTable({
  itemTimes,
  handleRefresh,
  isLoading,
}: {
  itemTimes: OrderItemTime[];
  handleRefresh: () => void;
  setAppliedFilters: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  selectedFilters: Record<string, string[]>;
  setSelectedFilters: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;

  isLoading: boolean;
  flagOptions: string[];
}): JSX.Element {
  const { selectedLocation } = useData();
  const columns = [
    {
      label: 'Item',
      key: 'description',
      type: 'string',
    },
    {
      label: 'Count',
      key: 'count',
      type: 'number',
    },
    {
      label: 'Item Time',
      key: 'itemTime',
      type: 'number',
      render: (order: OrderItemTime) => formatSeconds(order.itemTime),
    },
    {
      label: 'Avg Order Time',
      key: 'orderTimeWithItem',
      type: 'number',
      render: (order: OrderItemTime) => formatSeconds(order.orderTimeWithItem),
    },
  ];
  const [data, setData] = useState<DataType>(itemTimes);
  const [sortColumn, setSortColumn] = useState<TableColumn>(columns[0]);
  const [sortDirection, setSortDirection] = useState(1);
  const [isOpen, setIsOpen] = useState<boolean>(false);


  useEffect(() => {
    setData(itemTimes);
    handleSort(itemTimes, sortColumn, sortDirection);
  }, [itemTimes]);

  useEffect(() => {
    if (!isOpen) {
    }
  }, [isOpen]);

  function handleSort(
    data: DataType,
    column: TableColumn,
    sortDirection: number
  ): void {
    const newData = sortDataByColumn(data, column, sortDirection);
    setData(newData);
    setSortColumn(column);
    setSortDirection(sortDirection * -1);
  }

  

  const tableTitle = 'Item Times';

  if (isLoading) {
    return (
      <TableLoader
        columns={columns.map((column) => column.label)}
        title={tableTitle}
        handleRefresh={handleRefresh}
      />
    );
  }
  

  return (
      <TableContainer dataCy="orders-table">
        <TableHeader title="Order Items">
          <RefreshButton
            handleRefresh={handleRefresh}
            dataCy="refresh-button-orders"
          />
       
          
        </TableHeader>
        <Table>
          <TableHeadOrderItems
            columns={columns}
            handleSort={(col) => {
              handleSort(data, col, sortDirection);
            }}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
          />
          {selectedLocation !== null && (
            <TableBodyItemTimes
            columns={columns}
            data={data}
          />

          )}
        </Table>
      </TableContainer>

  );
}
